import { useContext } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Menu, Progress, Radio } from "antd";
import { MenuInfo } from "rc-menu/lib/interface";
import {
  TeamOutlined,
  ExpandOutlined,
  EllipsisOutlined,
  AlignLeftOutlined,
  ClockCircleOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";

import { Button } from "@/ui-components/commonComponents/Button/Button";
import { ReactComponent as ChevronDownSVG } from "@/assets/icons/feather-icons/chevron-down.svg";

import { ClientAbilityContext } from "@/shared/contexts/ability.context";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";

import { AssessmentStatusComponent } from "@/modules/assessments/components/assessment-status/assessment-status.component";
import { IAssessmentTable } from "@/modules/tables/TableAssessments";
import { AssessmentStatusFilter } from "@/modules/assessments/services/assessment-http.service";

import styles from "./assessment-list-mobile.module.css";

import "./index.css";

export function MobileAssessmentsListComponent({
  tableData,
  handleCreateNewAssessment,
  status,
  onStatusChange,
}: {
  tableData: IAssessmentTable[];
  handleCreateNewAssessment: (type: string) => void;
  status: AssessmentStatusFilter;
  onStatusChange: (status: AssessmentStatusFilter) => void;
}) {
  const ability = useContext(ClientAbilityContext);
  const canCreateAssessment = ability.can(Action.Create, Subject.ASSESSMENT);

  const handleMenuClick = (event: MenuInfo) => {
    handleCreateNewAssessment(event.key);
  };

  const menu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="new">Create New Assessment</Menu.Item>
      <Menu.Item key="from-template">Use Assessment Template</Menu.Item>
    </Menu>
  );

  return (
    <div>
      {canCreateAssessment && (
        <div className={styles.assessmentItemButton}>
          <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
            <Button rightIcon={<ChevronDownSVG aria-hidden="true" />}>new assessment</Button>
          </Dropdown>
        </div>
      )}

      <div className={styles.archiveFilterRow}>
        <Radio.Group
          name="assessment-status-filter"
          value={status}
          onChange={(e) => onStatusChange(e.target.value)}
          buttonStyle="solid"
          size="middle"
        >
          <Radio.Button value="all">All</Radio.Button>
          <Radio.Button value="active">Active</Radio.Button>
          <Radio.Button value="archived">Archived</Radio.Button>
        </Radio.Group>
      </div>

      {tableData.length > 0 ? (
        tableData.map(({ assessmentId, name, tests, questions, inProgress, finished, invited, timing, archivedOn }) => (
          <Link
            key={assessmentId}
            className={styles.assessmentItem}
            to={invited ? `${assessmentId}/invites` : `${assessmentId}/tests`}
          >
            <div className={styles.assessmentItemStatus}>
              <AssessmentStatusComponent isActive={!archivedOn} />
              <div className={styles.assessmentItemStatusSettings}>
                <ExpandOutlined aria-hidden="true" />
                <EllipsisOutlined aria-hidden="true" />
              </div>
            </div>

            <div className={styles.assessmentItemContent}>
              <h2 className={styles.assessmentItemContentTitle}>{name}</h2>
              <div className={styles.assessmentItemScores}>
                <div className={styles.assessmentItemScoresItem}>
                  <UnorderedListOutlined />
                  <p>{`${tests} ${tests === 1 ? "test" : "tests"}`}</p>
                </div>
                <div className={styles.assessmentItemScoresItem}>
                  <AlignLeftOutlined aria-hidden="true" />
                  <p>{`${questions} ${questions === 1 ? "question" : "questions"}`}</p>
                </div>
                <div className={styles.assessmentItemScoresItem}>
                  <ClockCircleOutlined aria-hidden="true" />
                  <p>{`${timing} min`}</p>
                </div>
                <div className={styles.assessmentItemScoresItem}>
                  <TeamOutlined aria-hidden="true" />
                  <p>{`${invited} ${invited === 1 ? "person" : "people"}`}</p>
                </div>
              </div>

              <div className={styles.assessmentItemScores}>
                <div className={styles.assessmentItemProgressItem}>
                  <p>{`${inProgress} in progress`}</p>
                  <Progress showInfo={false} percent={(inProgress / invited) * 100} />
                </div>
                <div className={styles.assessmentItemProgressItem}>
                  <p>{`${finished} finished`}</p>
                  <Progress showInfo={false} percent={(finished / invited) * 100} />
                </div>
              </div>
            </div>
          </Link>
        ))
      ) : (
        // Since nothing else is displayed if there are no assessments (no heading or table like the desktop view),
        // we're displaying an empty state describing the lack of assessments.
        <div className={styles.assessmentItemEmpty}>
          <p>No assessments found</p>
        </div>
      )}
    </div>
  );
}
